import * as THREE from "three";
import {DXF_VARS} from "../../Global/variables";

/**
 * Projects a 3D polygon to a 2D plane based on the polygon's highest point.
 * This method sorts the points and calculates the 2D projection using vector math.
 */
export function projectSegmentTo2D(polygon3D) {
    // Sort the polygon by the highest point along the Z axis
    let sortedPolygon = sortPolygonByHighestPoint(polygon3D);

    const pointA = sortedPolygon[0]; // Point A of the polygon
    const pointB = sortedPolygon[1]; // Point B of the polygon
    const pointC = sortedPolygon[2]; // Point C of the polygon

    // Calculate the edge vectors from points A to B and A to C
    const edge1 = new THREE.Vector3().subVectors(pointB, pointA);
    const edge2 = new THREE.Vector3().subVectors(pointC, pointA);

    // Compute the normal vector to the plane defined by these points
    const normal = new THREE.Vector3().crossVectors(edge1, edge2).normalize();

    // Define a basis for the 2D projection plane (u and v vectors)
    let u = new THREE.Vector3(1, 0, 0); // x-axis
    u.projectOnPlane(normal).normalize(); // Project u onto the plane defined by normal
    const v = new THREE.Vector3().crossVectors(normal, u).normalize(); // y-axis (perpendicular to u and normal)

    // Map each 3D point to a 2D point using the u and v vectors
    const polygon2D = sortedPolygon.map(point => {
        const x = point.dot(u); // Projection on u (x-coordinate in 2D)
        const y = point.dot(v); // Projection on v (y-coordinate in 2D)
        return new THREE.Vector2(x, y); // Create a 2D vector from the projection
    });

    // Store vectors for future use in DXF_VARS
    DXF_VARS.vectorU = u;
    DXF_VARS.vectorV = v;
    DXF_VARS.origin = polygon2D[0]; // Set the origin of the polygon to the first point

    // Reset polygon points and return the result
    return resetPolygonPoints(polygon2D);
}

/**
 * Sorts the points of the polygon by the highest point along the Z axis.
 * Returns the points in a clockwise or counterclockwise order based on the highest Z point.
 */
export function sortPolygonByHighestPoint(polygon3D) {
    let highestZIndex = 0;
    for (let i = 1; i < polygon3D.length; i++) {
        if (polygon3D[i].z > polygon3D[highestZIndex].z) {
            highestZIndex = i;
        }
    }

    // Determine the direction of sorting (clockwise or counterclockwise)
    let nextIndex = (highestZIndex === 0) ? polygon3D.length - 1 : highestZIndex - 1;
    let prevIndex = (highestZIndex === polygon3D.length - 1) ? 0 : highestZIndex + 1;
    let direction = (polygon3D[prevIndex].z > polygon3D[nextIndex].z) ? 1 : -1;

    // Rearrange the polygon points in the correct order
    let rearrangedArray = [];
    let currentIndex = highestZIndex;
    while (rearrangedArray.length < polygon3D.length) {
        let point = polygon3D[currentIndex];
        rearrangedArray.push(new THREE.Vector3(point.x, point.y, point.z)); // Add point to the new order
        currentIndex = (currentIndex + direction + polygon3D.length) % polygon3D.length;
    }

    // Close the polygon by adding the first point at the end
    rearrangedArray.push(rearrangedArray[0]);

    return rearrangedArray;
}

/**
 * Resets the polygon points to a new reference point (origin) and applies rotation.
 */
export function resetPolygonPoints(polygon) {
    // Save the reference point (the first point) for resetting the polygon
    const referencePoint = new THREE.Vector3(polygon[0].x, polygon[0].y, polygon[0].z);

    // Translate each point so the origin aligns with the first point
    const resetPolygonVertices = polygon.map(point => {
        return new THREE.Vector3(point.x , point.y, point.z).sub(referencePoint); // Subtract the reference point
    });

    // Align the polygon horizontally and return the rotated vertices
    let rotatedPolygonVertices = alignPolygonHorizontally(resetPolygonVertices);
    yFlipPolygon(rotatedPolygonVertices);

    return rotatedPolygonVertices;
}

/**
 * Aligns the polygon horizontally by rotating it to make the first point along the X-axis.
 */
export function alignPolygonHorizontally(polygonVertices) {
    let farthestPointIndex = 1;

    // Check if the farthest point is below the origin point
    const rotateDownward = polygonVertices[farthestPointIndex].y > polygonVertices[0].y;

    // Calculate the angle between the first point and the farthest point
    let lineAngle = Math.atan2(
        polygonVertices[farthestPointIndex].y - polygonVertices[0].y,
        polygonVertices[farthestPointIndex].x - polygonVertices[0].x
    );

    // Adjust the angle if the farthest point is below the origin
    if (rotateDownward) {
        lineAngle += Math.PI;
    } else {
        lineAngle -= Math.PI;
    }

    // Store the rotation angle for later use
    DXF_VARS.rotationAngle = lineAngle;

    // Apply the rotation to each point to align the polygon horizontally
    return polygonVertices.map(point => {
        const rotatedX = Math.cos(-lineAngle) * point.x - Math.sin(-lineAngle) * point.y;
        const rotatedY = Math.sin(-lineAngle) * point.x + Math.cos(-lineAngle) * point.y;
        return new THREE.Vector3(rotatedX, rotatedY, point.z);
    });
}

/**
 * Flips the polygon vertically if necessary to ensure the diagonal is correctly oriented.
 */
export function yFlipPolygon(rotatedPolygonVertices) {
    let isDiagonalHigher = false;

    // Check if any point in the polygon is higher than the origin point in the Y-axis
    for (let i = 2; i < rotatedPolygonVertices.length; i++) {
        if (rotatedPolygonVertices[i].y > rotatedPolygonVertices[0].y) {
            isDiagonalHigher = true;
            break;
        }
    }

    // If the diagonal is higher, flip the polygon vertically
    if (isDiagonalHigher) {
        rotatedPolygonVertices.forEach(point => {
            point.y = -point.y; // Flip the Y-coordinate of each point
        });
        DXF_VARS.isVerticalFlipped = true; // Mark that the polygon has been flipped
    } else {
        DXF_VARS.isVerticalFlipped = false; // No flip needed
    }
}
